import React, { useEffect } from 'react';
import { Form, Input, Switch, Button, Card, message, Tabs, Spin, Space, DatePicker, Select } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

export const PosterEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();

    const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

    const { data: posterData, isLoading } = useQuery({
        queryKey: ['adminPoster', id],
        queryFn: async () => {
            const response = await apiClient.get(`/admin/posters/${id}`);
            return response.data.data;
        },
        enabled: isEditMode
    });

    useEffect(() => {
        if (posterData) {
            const formValues: any = {
                imageUrl: posterData.imageUrl,
                redirectUrl: posterData.redirectUrl || posterData.pageUrl,
                category: posterData.category || 'general',
                isActive: posterData.isActive,
            };

            if (posterData.publishStartDate || posterData.publishEndDate) {
                formValues.publishPeriod = [
                    posterData.publishStartDate ? dayjs(posterData.publishStartDate) : null,
                    posterData.publishEndDate ? dayjs(posterData.publishEndDate) : null,
                ];
            }
            if (posterData.eventDate) {
                formValues.eventDate = dayjs(posterData.eventDate);
            }

            appLanguages.forEach(lang => {
                const trans = posterData.translations.find((t: any) => t.languageCode === lang);
                formValues[`translations_${lang}`] = trans ? {
                    title: trans.title,
                    description: trans.description
                } : {};
            });

            form.setFieldsValue(formValues);
        } else if (!isEditMode) {
            form.setFieldsValue({ isActive: true, category: 'general' });
        }
    }, [posterData, form, isEditMode]);

    const saveMutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = appLanguages.map(lang => {
                const t = values[`translations_${lang}`];
                
                // If form values exist and have a title, use them
                if (t && t.title) {
                    return {
                        languageCode: lang,
                        title: t.title,
                        description: t.description || null
                    };
                }

                // Fallback to original database values if the tab/fields were never mounted/submitted (undefined)
                const originalTrans = posterData?.translations?.find((x: any) => x.languageCode === lang);
                if (originalTrans && t === undefined) {
                    return {
                        languageCode: lang,
                        title: originalTrans.title,
                        description: originalTrans.description || null
                    };
                }

                return null;
            }).filter(Boolean);

            if (translations.length === 0) {
                throw new Error("At least one language translation with a Title is required.");
            }

            const publishStartDate = values.publishPeriod?.[0] ? values.publishPeriod[0].toISOString() : null;
            const publishEndDate = values.publishPeriod?.[1] ? values.publishPeriod[1].toISOString() : null;
            const eventDate = values.eventDate ? values.eventDate.toISOString() : null;

            const payload = {
                id: isEditMode ? id : undefined,
                imageUrl: values.imageUrl,
                redirectUrl: values.redirectUrl || null,
                pageUrl: values.redirectUrl || null, // API handles backend URL based on this
                category: values.category,
                isActive: values.isActive,
                publishStartDate,
                publishEndDate,
                eventDate,
                translations
            };

            if (isEditMode) {
                await apiClient.put(`/admin/posters/${id}`, payload);
            } else {
                await apiClient.post('/admin/posters', payload);
            }
        },
        onSuccess: () => {
            message.success(`Poster ${isEditMode ? 'updated' : 'created'} successfully.`);
            queryClient.invalidateQueries({ queryKey: ['adminPosters'] });
            navigate('/admin/posters');
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || error.message || 'Failed to save poster.');
        }
    });

    if (isEditMode && isLoading) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    const tabItems = appLanguages.map(lang => ({
        key: lang,
        label: lang.toUpperCase(),
        forceRender: true,
        children: (
            <div style={{ display: 'block', width: '100%' }}>
                <Form.Item name={[`translations_${lang}`, 'title']} label="Title">
                    <Input placeholder={`Title in ${lang.toUpperCase()}`} />
                </Form.Item>
                <Form.Item name={[`translations_${lang}`, 'description']} label="Description">
                    <TextArea rows={4} placeholder="Optional short description..." />
                </Form.Item>
            </div>
        )
    }));

    return (
        <Card title={isEditMode ? 'Edit Poster' : 'Create New Poster'}>
            <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)}>

                {/* Core Properties */}
                <div style={{ marginBottom: 24, width: '100%' }}>
                    <Form.Item name="imageUrl" label="Image URL (Internal/External)" rules={[{ required: true, message: 'Image URL is required' }]}>
                        <Input placeholder="e.g. /api/media/download/123e4567..." />
                    </Form.Item>
                    <Form.Item name="redirectUrl" label="Redirect Link URL (Optional)">
                        <Input placeholder="e.g. https://example.com/tickets" />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                        <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                            <Select style={{ width: 180 }}>
                                <Select.Option value="general">Genel (General)</Select.Option>
                                <Select.Option value="youth">Gençlik (Youth)</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="isActive" label="Status" valuePropName="checked">
                            <Switch checkedChildren="Active" unCheckedChildren="Draft" />
                        </Form.Item>
                        <Form.Item name="eventDate" label="Event Date">
                            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: 200 }} />
                        </Form.Item>
                        <Form.Item name="publishPeriod" label="Publish Visibility Period (Start - End)">
                            <RangePicker showTime format="YYYY-MM-DD HH:mm" />
                        </Form.Item>
                    </div>
                </div>

                {/* Translation Properties */}
                <Card type="inner" title="Translations" style={{ marginBottom: 24 }}>
                    <Tabs type="card" items={tabItems} />
                </Card>

                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
                            Save Poster
                        </Button>
                        <Button onClick={() => navigate('/admin/posters')}>Cancel</Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};
